import type { GuestBrowserCapability } from '../../guestBrowserCapability';
import type { LatestRvfcObservation } from '../../decodeHintCollector';
import { GuestStreamStatsSampler } from '../../guestStreamStatsSampler';
import { ReceiverCounterTracker } from '../../streamQuality/receiverCounterTracker';
import type { ITuningClock, ITuningTelemetrySource } from '../contracts/ports';
import type { TuningHistogram, TuningMetric, TuningTelemetryContext, TuningTelemetryWindow } from '../contracts/measurements';
import { TUNING_TELEMETRY_MAX_BYTES } from '../contracts/measurements';
import { tuningJsonBytes } from '../contracts/validation';
import type { GuestEncodedTimingBatch, GuestReportObservation, IGuestReportScheduler, IGuestTelemetryObserver, IGuestTuningState } from './GuestTelemetryPorts';
import { GuestHistogramReducer } from './GuestHistogramReducer';
import { TelemetryIncidentRing } from './TelemetryIncidentRing';

type Options = { clock: ITuningClock; state: IGuestTuningState; capability?: GuestBrowserCapability;
  isMobile?: boolean; scheduler?: IGuestReportScheduler };
type ObservationClock = TuningTelemetryWindow['clock'];
const number = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const metric = (value: number | undefined, unit: TuningMetric['unit'], source: string,
  semantics: TuningMetric['semantics'] = 'gauge'): TuningMetric => ({ value: value ?? null, unit, source,
    availability: value === undefined ? 'missing' : 'measured', sampleCount: value === undefined ? 0 : 1, semantics });

/** Diagnostic reducers never drain or advance the production sampler's baselines. */
export class GuestTelemetrySource implements IGuestTelemetryObserver, ITuningTelemetrySource {
  private readonly sampler: GuestStreamStatsSampler;
  private readonly counters = new ReceiverCounterTracker();
  private readonly listeners = new Set<(window: TuningTelemetryWindow) => void>();
  private readonly ring = new TelemetryIncidentRing();
  private readonly histograms = new Map<string, GuestHistogramReducer>();
  private latestMetrics: Record<string, TuningMetric> = {};
  private timer: ReturnType<typeof setInterval> | undefined;
  private windowMs = 250;
  private statsIntervalMs = 250;
  private epoch = 0;
  private path = 0;
  private sequence = 0;
  private windowStart = 0;
  private histogramSentAt = 0;
  private lastReportAt: number | undefined;
  private lastPresentation: number | undefined;
  private lastFrameSequence: number | undefined;
  private lastCompositorCount: number | undefined;
  private callbacks = 0;
  private compositorDelta = 0;
  private dropped = 0;
  private sampleSkipped = 0;
  private histogramDroppedSamples = 0;
  private histogramCursor = 0;
  private visibility = 'unknown';
  private clock: ObservationClock = { state: 'uncalibrated' };
  private context: TuningTelemetryContext = {};
  private frameSampling = true;
  private latestFrame?: LatestRvfcObservation;
  private calibrationReceivedAtMs = 0;

  constructor(private readonly deps: Options) {
    this.sampler = new GuestStreamStatsSampler({ nowMs: () => deps.clock.monotonicMs(),
      capability: deps.capability, isMobile: deps.isMobile, debugLogging: false });
  }
  subscribe(listener: (window: TuningTelemetryWindow) => void): () => void {
    this.listeners.add(listener); return () => { this.listeners.delete(listener); };
  }
  arm(enabled: boolean): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    this.deps.scheduler?.configureTuningSampling(enabled ? this.statsIntervalMs : 1_000);
    if (!enabled) return;
    this.windowStart = this.deps.clock.monotonicMs(); this.histogramSentAt = this.windowStart;
    this.timer = setInterval(() => this.flush(), this.windowMs);
  }
  configure(settings: { windowMs?: number; statsIntervalMs?: number }): void {
    for (const value of Object.values(settings)) if (value !== undefined && ![250, 500, 1_000].includes(value)) throw new RangeError('Unsupported telemetry cadence');
    this.windowMs = settings.windowMs ?? this.windowMs; this.statsIntervalMs = settings.statsIntervalMs ?? this.statsIntervalMs;
    if (this.timer) this.arm(true);
  }
  configuration(): { windowMs: number; statsIntervalMs: number } { return { windowMs: this.windowMs, statsIntervalMs: this.statsIntervalMs }; }
  configureFrameSampling(enabled: boolean): void {
    this.frameSampling = enabled; this.resetHistograms(); this.lastPresentation = undefined;
    this.latestFrame = undefined;
    this.callbacks = 0; this.compositorDelta = 0;
  }
  observeSkippedSample(epoch: number): void { if (this.timer && epoch === this.epoch) this.sampleSkipped++; }
  setContext(visibility: string, clock: ObservationClock = this.clock): void { this.visibility = visibility; this.clock = { ...clock }; }
  setCalibrationClock(clock: ObservationClock): void {
    this.clock = { ...clock }; this.calibrationReceivedAtMs = this.deps.clock.monotonicMs();
  }
  setEndpointMetadata(context: TuningTelemetryContext): void { this.context = { ...this.context, ...context }; }
  observeAvailability(name: string, availability: Exclude<TuningMetric['availability'], 'measured'>): void {
    this.latestMetrics[`${name}.max`] = { value: null, unit: 'ms', source: name, availability, sampleCount: 0, semantics: 'gauge' };
  }
  observeReport(observation: GuestReportObservation): void {
    if (!this.timer || observation.endpointConnectionEpoch !== this.epoch) return;
    if (observation.pathEpoch !== this.path) {
      this.sampler.resetForPath(); this.counters.reset(); this.resetHistograms(); this.path = observation.pathEpoch;
      this.setCalibrationClock({ state: 'uncalibrated' });
    }
    this.lastReportAt = observation.completedAtMs;
    const sample = this.sampler.sample(observation.report);
    this.counters.observe(observation.report);
    this.latestMetrics['statsDuration'] = metric(observation.completedAtMs - observation.requestedAtMs, 'ms', 'getStats');
    this.latestMetrics['statsSchedulingDelay'] = metric(Math.max(0, observation.requestedAtMs - observation.scheduledAtMs), 'ms', 'timer');
    if (!sample) return;
    const fields = sample.wire as Record<string, unknown>;
    const mapping: Record<string, TuningMetric['unit']> = { decodeFps: 'fps', rttMs: 'ms', jitterMs: 'ms',
      decodeMsPerFrame: 'ms', packetsLostPct: 'percent', jitterBufferDelayMs: 'ms',
      audioJitterBufferDelayMs: 'ms', audioBitrateBps: 'bps', frameWidth: 'count', frameHeight: 'count' };
    for (const [key, unit] of Object.entries(mapping)) {
      const field = fields[key === 'jitterBufferDelayMs' ? 'jbDelayMs' : key];
      this.latestMetrics[key] = metric(number(field) ? field : undefined, unit, 'getStats');
    }
    this.latestMetrics['receiveBitrate'] = metric(number(sample.raw.bitsPerSecond) ? sample.raw.bitsPerSecond : undefined, 'bps', 'getStats');
    if (this.deps.capability?.exposesJitterBufferDelay === false) this.latestMetrics['jitterBufferDelayMs'] = {
      ...this.latestMetrics['jitterBufferDelayMs'], value: null, availability: 'unsupported' };
    this.context = { ...this.context, codec: sample.wire.codecMimeType, decoderImplementation: sample.wire.decoderImplementation,
      route: sample.selectedPath?.selection === 'selected' && typeof sample.selectedPath.relay === 'boolean' ?
        sample.selectedPath.relay ? 'relay' : 'direct' : 'unknown' };
    const counterSummary = this.counters.summarizeAndReset();
    if (counterSummary) for (const [key, count] of Object.entries(counterSummary)) this.latestMetrics[key] =
      metric((this.latestMetrics[key]?.value ?? 0) + count, key === 'freezeMs' ? 'ms' : 'count', 'inbound-rtp', 'interval-delta');
  }
  observeFrame(observation: LatestRvfcObservation): void {
    if (!this.frameSampling) return;
    if (!this.timer) return;
    this.latestFrame = observation;
    if (this.lastFrameSequence !== undefined && observation.observationSequence <= this.lastFrameSequence) return;
    this.lastFrameSequence = observation.observationSequence;
    const now = this.deps.clock.monotonicMs();
    const presented = observation.presentationTime ?? observation.displayNow;
    if (!number(presented)) return;
    this.callbacks++;
    if (this.lastPresentation !== undefined && presented >= this.lastPresentation) {
      this.observeTiming('presentationGap', presented - this.lastPresentation, now);
      if (presented - this.lastPresentation >= 100) this.ring.trigger(now);
    }
    this.lastPresentation = presented;
    if (number(observation.presentedFrames)) {
      if (this.lastCompositorCount !== undefined && observation.presentedFrames >= this.lastCompositorCount) this.compositorDelta += observation.presentedFrames - this.lastCompositorCount;
      this.lastCompositorCount = observation.presentedFrames;
    }
    if (number(observation.receiveTime) && number(observation.presentationTime)) this.observeTiming('receiveToComposition', observation.presentationTime - observation.receiveTime, now);
    if (number(observation.captureTime) && number(observation.presentationTime)) this.observeTiming('captureToComposition', observation.presentationTime - observation.captureTime, now);
    if (number(observation.processingDuration)) this.observeTiming('processingDuration', observation.processingDuration * 1_000, now);
    if (number(observation.presentationTime)) this.observeTiming('callbackLateness', (observation.callbackMonotonicMs ?? now) - observation.presentationTime, now);
    if (number(observation.expectedDisplayTime) && number(observation.presentationTime)) this.observeTiming('compositionToExpectedDisplay', observation.expectedDisplayTime - observation.presentationTime, now);
  }
  observeEncodedBatch(batch: GuestEncodedTimingBatch): void {
    if (!this.timer) return;
    this.latestMetrics['encodedFrames'] = metric(batch.frames, 'count', 'encoded-transform', 'interval-delta');
    this.latestMetrics['keyframes'] = metric(batch.keyframes, 'count', 'encoded-transform', 'interval-delta');
    this.latestMetrics['maxEncodedBytes'] = metric(batch.maxBytes, 'bytes', 'encoded-transform');
    this.latestMetrics['maxKeyframeBytes'] = metric(batch.keyframes > 0 ? batch.maxKeyframeBytes : undefined, 'bytes', 'encoded-transform');
    const originOffset = batch.workerWallAnchorMs - (this.deps.clock.wallMs() - this.deps.clock.monotonicMs());
    let arrival = this.histograms.get('encodedArrivalGap');
    if (!arrival) { arrival = new GuestHistogramReducer('encodedArrivalGap'); this.histograms.set('encodedArrivalGap', arrival); }
    if (batch.gapSamples > 0 && !arrival.merge({ schema: 'ms-v1', unit: 'ms', source: 'encodedArrivalGap', availability: 'measured',
      monotonicStartMs: batch.monotonicStartMs + originOffset, monotonicEndMs: batch.monotonicEndMs + originOffset,
      buckets: batch.gapBuckets, sampleCount: batch.gapSamples, maxMs: batch.maxGapMs })) this.histogramDroppedSamples += batch.gapSamples;
    this.latestMetrics['encodedArrivalGap.max'] = metric(batch.maxGapMs, 'ms', 'encoded-transform');
    this.observeTiming('workerMessageDelay', Math.max(0, batch.pageReceivedAtMs - (batch.monotonicEndMs + originOffset)), batch.pageReceivedAtMs);
    this.observeTiming('workerFlushDelay', batch.flushLateMs, batch.pageReceivedAtMs);
  }
  observeTiming(name: string, valueMs: number, nowMs = this.deps.clock.monotonicMs()): void {
    if (!this.timer || !number(valueMs) || valueMs < 0) return;
    let histogram = this.histograms.get(name);
    if (!histogram) { histogram = new GuestHistogramReducer(name); this.histograms.set(name, histogram); }
    histogram.observe(valueMs, nowMs);
    this.latestMetrics[`${name}.max`] = metric(Math.max(this.latestMetrics[`${name}.max`]?.value ?? 0, valueMs), 'ms', name);
  }
  flush(): TuningTelemetryWindow | undefined {
    const now = this.deps.clock.monotonicMs();
    if (!this.timer || now < this.windowStart) { this.resetForConnection(this.epoch); return undefined; }
    const span = now - this.windowStart;
    const metrics: Record<string, TuningMetric> = { ...this.latestMetrics,
      callbacks: this.frameSampling ? metric(this.callbacks, 'count', 'rvfc', 'interval-delta')
        : { ...metric(undefined, 'count', 'rvfc', 'interval-delta'), availability: 'not-sampled' },
      statsAge: metric(this.lastReportAt === undefined ? undefined : now - this.lastReportAt, 'ms', 'getStats'),
      presentedFps: metric(this.lastPresentation === undefined ? undefined : (this.compositorDelta || this.callbacks) * 1_000 / Math.max(1, span), 'fps', 'rvfc'),
      sinceLastPresent: metric(this.lastPresentation === undefined ? undefined : Math.max(0, now - this.lastPresentation), 'ms', 'rvfc'),
      histogramDroppedSamples: metric(this.histogramDroppedSamples, 'count', 'observer', 'cumulative') };
    if (!this.frameSampling) for (const key of ['presentedFps', 'sinceLastPresent']) metrics[key] = { ...metrics[key], availability: 'not-sampled' };
    if (this.lastReportAt !== undefined && now - this.lastReportAt > Math.max(2_000, this.statsIntervalMs * 2)) {
      for (const key of Object.keys(metrics)) if (metrics[key].source === 'getStats' || metrics[key].source === 'inbound-rtp') metrics[key] = { ...metrics[key], availability: 'stale', value: null };
    }
    const distributions: Record<string, TuningHistogram> = {};
    const histogramCandidates = new Map<string, TuningHistogram>();
    const frame = this.latestFrame;
    if (frame?.timingSource === 'presentation-time' && number(frame.rtpTimestamp) && number(frame.decodeWallMs)
      && number(frame.callbackMonotonicMs) && now - frame.callbackMonotonicMs >= 0 && now - frame.callbackMonotonicMs < 2_000) {
      metrics['frame.rtpTimestamp'] = metric(frame.rtpTimestamp, 'count', 'rvfc-presentation-time');
      metrics['frame.compositionWallMs'] = metric(frame.decodeWallMs, 'ms', 'rvfc-presentation-time');
      metrics['frame.observationAgeMs'] = metric(now - frame.callbackMonotonicMs, 'ms', 'rvfc-presentation-time');
    }
    if (now - this.histogramSentAt >= 1_000) {
      for (const [key, histogram] of this.histograms) histogramCandidates.set(key,
        histogram.snapshot({ startMs: this.histogramSentAt, endMs: now }));
      this.histogramSentAt = now;
    }
    const clock = { ...this.clock };
    if (clock.ageMs !== undefined) clock.ageMs += Math.max(0, now - this.calibrationReceivedAtMs);
    if (clock.state === 'calibrated' && (clock.ageMs === undefined || clock.ageMs > 60_000)) clock.state = 'stale';
    const window: TuningTelemetryWindow = { version: 1, target: { ...this.deps.state.snapshot().target }, endpoint: 'guest',
      sequence: ++this.sequence, monotonicStartMs: this.windowStart, monotonicEndMs: now,
      wallAnchorMs: this.deps.clock.wallMs(), clock, pathEpoch: this.path, endpointConnectionEpoch: this.epoch,
      metrics, distributions, telemetryDropped: this.dropped, sampleSkipped: this.sampleSkipped, visibility: this.visibility };
    window.context = Object.fromEntries(Object.entries({ ...this.context, configurationHash: this.deps.state.snapshot().configurationHash })
      .filter(([, value]) => typeof value === 'string' && value.length > 0 && value.length <= 160)) as TuningTelemetryContext;
    const core = ['presentedFps', 'sinceLastPresent', 'decodeFps', 'rttMs', 'statsAge', 'presentationGap.max', 'histogramDroppedSamples',
      'frame.rtpTimestamp', 'frame.compositionWallMs', 'frame.observationAgeMs'];
    const budget = histogramCandidates.size ? 3_400 : 1_400;
    const optional = Object.keys(metrics).filter((key) => !core.includes(key));
    const rotate = optional.length ? (this.sequence * 5) % optional.length : 0;
    for (const key of [...optional.slice(rotate), ...optional.slice(0, rotate)]) {
      if (tuningJsonBytes({ type: 'stream-tuning', version: 1, kind: 'telemetry', payload: window }) <= budget) break;
      delete metrics[key];
    }
    if (histogramCandidates.size) {
      const required = ['presentationGap', 'captureToComposition'];
      const optional = [...histogramCandidates.keys()].filter(key => !required.includes(key));
      const rotate = optional.length ? this.histogramCursor++ % optional.length : 0;
      const ordered = [...required.filter(key => histogramCandidates.has(key)),
        ...optional.slice(rotate), ...optional.slice(0, rotate)];
      for (const key of ordered) {
        const candidate = histogramCandidates.get(key)!; distributions[key] = candidate;
        if (tuningJsonBytes({ type: 'stream-tuning', version: 1, kind: 'telemetry', payload: window }) <= TUNING_TELEMETRY_MAX_BYTES) {
          this.histograms.get(key)?.flush({ startMs: candidate.monotonicStartMs, endMs: candidate.monotonicEndMs });
          continue;
        }
        delete distributions[key];
        if (!required.includes(key)) continue;
        this.histograms.get(key)?.flush({ startMs: candidate.monotonicStartMs, endMs: candidate.monotonicEndMs });
        this.histogramDroppedSamples += candidate.sampleCount; this.dropped++;
      }
    }
    metrics['histogramDroppedSamples'] = metric(this.histogramDroppedSamples, 'count', 'observer', 'cumulative');
    window.telemetryDropped = this.dropped;
    Object.values(metrics).forEach(Object.freeze);
    for (const histogram of Object.values(distributions)) {
      histogram.buckets.forEach(Object.freeze); Object.freeze(histogram.buckets); Object.freeze(histogram);
    }
    Object.freeze(metrics); Object.freeze(distributions); Object.freeze(window.target); Object.freeze(window.clock);
    if (window.context) Object.freeze(window.context); Object.freeze(window);
    this.ring.append(window);
    for (const listener of this.listeners) { try { listener(window); } catch { /* Observer failures are isolated. */ } }
    this.windowStart = now; this.callbacks = 0; this.compositorDelta = 0;
    for (const key of Object.keys(this.latestMetrics)) {
      if (this.latestMetrics[key].semantics === 'interval-delta' && metrics[key] !== undefined) delete this.latestMetrics[key];
      else if (key.endsWith('.max') && this.latestMetrics[key].availability === 'measured') delete this.latestMetrics[key];
    }
    return window;
  }
  incident(): ReturnType<TelemetryIncidentRing['snapshot']> { return this.ring.snapshot(); }
  resetForConnection(epoch: number): void {
    this.epoch = epoch; this.path = 0; this.sampler.reset(); this.counters.reset(); this.resetHistograms();
    this.dropped = 0; this.histogramDroppedSamples = 0; this.histogramCursor = 0; this.sampleSkipped = 0;
    this.latestMetrics = {}; this.lastReportAt = undefined; this.lastPresentation = undefined; this.latestFrame = undefined;
    this.context = { buildId: this.context.buildId, runtimeId: this.context.runtimeId, policyVersion: this.context.policyVersion };
    this.lastFrameSequence = undefined; this.lastCompositorCount = undefined; this.callbacks = 0; this.compositorDelta = 0;
    this.windowStart = this.deps.clock.monotonicMs(); this.histogramSentAt = this.windowStart; this.ring.reset();
  }
  dispose(): void { this.arm(false); this.listeners.clear(); this.resetForConnection(this.epoch); }
  private resetHistograms(): void { for (const histogram of this.histograms.values()) histogram.reset(); }
}
